/* eslint-disable react/prop-types */
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import {
  DeleteOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Spin,
  Tabs,
  Typography,
  message,
} from 'antd';
import Direccion from 'components/Direccion';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import Table from 'components/Table';
import Uploader from 'components/Uploader';
import makeMessager from 'utils/complexFormsMessages';
import { onError } from 'utils/handlers';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import { getTiposDeAnuncios } from 'api/catalogos/tiposDeAnuncios';
import { getPredioCaja } from 'api/catastro';
import { getContribuyenteCaja } from 'api/empresas/contribuyentes';
import { getEmpresa, getEmpresas } from 'api/empresas/empresas';
import {
  baseURI,
  deleteExpedienteDeAnuncios,
  getExpedienteDeAnuncios,
  getExpedienteDeAnunciosCaja,
  patchExpedienteDeAnuncios,
  postExpedienteDeAnuncios,
} from 'api/empresas/expedienteDeAnuncios';
import { InputSearchPro } from 'components/InputSearch';
import address from 'utils/address';
import padronToString from 'utils/padronesToString';
import { ActionsNames, getModelPermission, hasActionPermission } from 'utils/permission/index';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import { discartNullValues } from 'utils/formatValues';

const { TabPane } = Tabs;

const models = ['aseguradora'];

const ExpedienteDeAnuncio = ({ permission }) => {
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const [form] = Form.useForm();
  const [formSearch] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [formAddress, setFormAddress] = useState();
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [], count: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [empresaFound, setEmpresaFound] = useState([]);
  const [tipoDePadron, setTipoDePadron] = useState(padronesEnum.EMPRESA);

  const [tiposDeAnuncios, setTiposDeAnuncios] = useState([]);
  const [contribuyenteFound, setContribuyenteFound] = useState([]);
  const [predioFound, setPredioFound] = useState([]);
  const [current, setCurrent] = useState(null);

  const basicKeys = ['general', 'conceptos'];
  const complexForms = [{
    key: 'direccion_anuncio',
    formInstance: formAddress,
    permission: addressPermission,
  }];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getExpedienteDeAnunciosCaja({
      ...q,
      page,
    });
    setData(_data);
    setLoading(false);
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  useEffect(() => {
    let mounted = true;
    let _hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        _hasAllPermission = _hasAllViewPermission;
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const [_tiposDeAnuncios] = await Promise.all([
        getTiposDeAnuncios(),
      ]);
      if (mounted) {
        setTiposDeAnuncios(_tiposDeAnuncios);
        setLoading(false);
      }
    };
    validatePermission();
    if (_hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);

  const fetchEmpresa = async (id) => {
    try {
      setLoading(true);
      if (id) {
        const _empresa = await getEmpresa(id?.id || id);
        if (_empresa) {
          setEmpresaFound({
            ..._empresa,
            nombre_comercial: `${_empresa?.clave} - ${_empresa?.nombre_comercial}`,
          });
          setTimeout(() => {
            form.setFieldsValue({
              empresa: _empresa.id,
            });
          });
        } else {
          message.info('No se encontro la empresa');
        }
        setLoading(false);
      }
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const resetAndValidate = () => {
    setEmpresaFound([]);
    form.resetFields(['q']);
    form.validateFields(['q']);
  };

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    formSearch.resetFields();
    setSelectedRowKeys([]);
    setEmpresaFound([]);
    setCurrentTabKey('general');
    setTipoDePadron(padronesEnum.EMPRESA);
    setContribuyenteFound([]);
    setPredioFound([]);
    setCurrent(null);
  };

  const onClickEdit = async (ExpedienteDeAnuncioID) => {
    const key = selectedRowKeys[0] || ExpedienteDeAnuncioID;
    const match = await getExpedienteDeAnuncios(key);
    if (match) {
      await fetchEmpresa(match.empresa?.id);
      setTipoDePadron(padronesEnum.EMPRESA);
      if (match?.contribuyente) {
        setContribuyenteFound([match.contribuyente]);
      }
      if (match?.predio) {
        setPredioFound([match.predio]);
      }
      if (match?.empresa) {
        setEmpresaFound([match.empresa]);
      }
      form.setFieldsValue(match);
    }
    setVisible(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formInfo.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formInfo.getFieldsValue(),
      };
      values.empresa = empresaFound[0]?.id || null;
      values.contribuyente = contribuyenteFound[0]?.id || null;
      values.predio = predioFound[0]?.id || null;
      values.nombre_completo_propietario = contribuyenteFound[0]
        ? padronToString(contribuyenteFound[0], padronesEnum.CONTRIBUYENTE) : null;

      const [key] = selectedRowKeys;
      const _data = await (key ? patchExpedienteDeAnuncios(key, values)
        : postExpedienteDeAnuncios(values));

      if (_data) {
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
            setCurrent(_data);
          }
          await onClickEdit(_data.id);
        } else {
          onCancel();
        }
        setLoading(false);
        return _data;
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form, formInfo], setCurrentTabKey);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteExpedienteDeAnuncios(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setCurrent(record.id);
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const searchEmpresa = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      const { q_empresa } = formSearch.getFieldsValue(['q_empresa']);
      if (q || q_empresa) {
        const _empresaFound = await getEmpresas({ q: q || q_empresa });
        if (_empresaFound.results.length) {
          const [selected] = _empresaFound.results;
          form.setFieldsValue({
            empresa: selected.id,
          });
          formSearch.setFieldsValue({
            empresa: selected.id,
          });
          setEmpresaFound([selected]);
        } else if (!_empresaFound.results.length) {
          message.info('Empresa no encontrada');
          setVisibleBusquedaPadrones(true);
        }
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const resetAndValidateEmpresa = () => {
    setEmpresaFound([]);
    form.resetFields(['q_empresa']);
    form.validateFields(['q_empresa']);
    formSearch.resetFields(['q_empresa']);
    formSearch.validateFields(['q_empresa']);
  };

  const searchContribuyente = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q_contribuyente']);
      const { q_contribuyente } = form.getFieldsValue(['q_contribuyente']);
      const { q_contribuyente: q_contribuyenteSearch } = formSearch.getFieldsValue(['q_contribuyente']);
      if (!q_contribuyente && !q_contribuyenteSearch) {
        message.info('Ingrese al menos un dato para realizar la búsqueda');
        return;
      }

      const _contribuyenteFound = await getContribuyenteCaja({
        q: q_contribuyente || q_contribuyenteSearch,
      });
      if (!_contribuyenteFound?.results?.length) {
        message.info('No se encontró al contribuyente');
        return;
      }

      const [selected] = _contribuyenteFound.results;
      form.setFieldsValue({
        contribuyente: selected.id,
      });
      formSearch.setFieldsValue({
        contribuyente: selected.id,
      });
      setContribuyenteFound([selected]);
    } catch (error) {
      onError(error, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const resetAndValidateContribuyente = () => {
    setContribuyenteFound([]);
    form.resetFields(['q_contribuyente']);
    form.validateFields(['q_contribuyente']);
    formSearch.resetFields(['q_contribuyente']);
    formSearch.validateFields(['q_contribuyente']);
  };

  const searchPredio = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q_predio']);
      const { q_predio } = form.getFieldsValue(['q_predio']);
      const { q_predio: q_predioSearch } = formSearch.getFieldsValue(['q_predio']);
      if (!q_predio && !q_predioSearch) {
        message.info('Ingrese al menos un dato para realizar la búsqueda');
        return;
      }

      const _predioFound = await getPredioCaja({ q: q_predio || q_predioSearch });
      if (!_predioFound?.results?.length) {
        message.info('No se encontró el predio');
        return;
      }

      const [selected] = _predioFound.results;
      form.setFieldsValue({ predio: selected.id });
      setPredioFound([selected]);
    } catch (error) {
      onError(error, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const resetAndValidatePredio = () => {
    setPredioFound([]);
    form.resetFields(['q_predio']);
    form.validateFields(['q_predio']);
    formSearch.resetFields(['q_predio']);
    formSearch.validateFields(['q_predio']);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 200,
    },
    {
      titleText: 'Tipo de Anuncio',
      dataIndex: 'tipo_de_anuncio',
      key: 'tipo_de_anuncio',
      width: 150,
      catalog: {
        cat: tiposDeAnuncios,
        labelProp: 'descripcion',
      },
    },
    {
      titleText: 'Dirección de Anuncio',
      dataIndex: 'direccion_anuncio',
      key: 'direccion_anuncio',
      width: 300,
      render: (val) => address(val),
    },
    {
      titleText: 'Contribuyente',
      dataIndex: 'nombre_completo_propietario',
      key: 'nombre_completo_propietario',
      width: 500,
    },
    {
      titleText: 'Comercio',
      dataIndex: 'empresa__nombre_comercial',
      key: 'empresa__nombre_comercial',
      width: 500,
    },
    {
      titleText: 'Número de Licencia',
      dataIndex: 'numero_licencia',
      key: 'numero_licencia',
      width: 200,
    },
  ];

  const rules = {
    required: [
      { required: true },
    ],
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys, complexForms, currentTabKey, setCurrentTabKey, showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  const keyEnum = {
    [padronesEnum.EMPRESA]: 'empresa',
    [padronesEnum.CONTRIBUYENTE]: 'contribuyente',
    [padronesEnum.PREDIO]: 'predio',
  };

  const callbackBusquedaPadrones = (values) => {
    const key = keyEnum[tipoDePadron];
    form.setFieldsValue({ [key]: values.id });
    formSearch.setFieldsValue({ [key]: values.id });
    if (tipoDePadron === padronesEnum.EMPRESA) {
      setEmpresaFound([values]);
    } else if (tipoDePadron === padronesEnum.CONTRIBUYENTE) {
      setContribuyenteFound([values]);
    } else if (tipoDePadron === padronesEnum.PREDIO) {
      setPredioFound([values]);
    }
  };

  const onFinishSearch = async () => {
    const values = discartNullValues(formSearch.getFieldsValue());
    if (!Object.keys(values).length) {
      message.info('Ingrese al menos un dato para realizar la búsqueda');
      setData({ results: [], count: 0 });
      setCurrentPage(1);
      return;
    }
    await fetchData(1, values);
  };

  const handleDireccionCallback = useCallback((values, _continue) => {
    if (!_continue) {
      onCancel();
    } else {
      const [key] = selectedRowKeys;
      if (!key) {
        message.error('No se puede actualizar la dirección sin un expediente seleccionado');
        return;
      }
      const clone = [...data.results];
      const mappedData = clone.map((e) => (e.id === key
        ? ({ ...e, direccion_anuncio: values?.id }) : e));
      setData({ ...data, results: mappedData });
    }
  }, [selectedRowKeys, data, onCancel]);

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible
          ? (
            <>
              <Form form={formSearch} onFinish={onFinishSearch} layout="vertical">
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="clave"
                      label="clave"
                      hasFeedback
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="tipo_de_anuncio"
                      label="Tipo de Anuncio"
                      hasFeedback
                    >
                      <Select dataSource={tiposDeAnuncios} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="numero_licencia"
                      label="Número de Licencia"
                      hasFeedback
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <InputSearchPro
                    label="Contribuyente"
                    name="q_contribuyente"
                    onClickAdvanced={() => {
                      setTipoDePadron(padronesEnum.CONTRIBUYENTE);
                      setVisibleBusquedaPadrones(true);
                    }}
                    breakPoints={{
                      xs: 24,
                      sm: 24,
                      md: 8,
                    }}
                    inputProps={{
                      onPressEnter: () => searchContribuyente(),
                      onChange: ({ target: { value } }) => {
                        if (!value) {
                          resetAndValidate();
                        }
                      },
                    }}
                    resultProps={{
                      breakPoints: {
                        xs: 24,
                        sm: 24,
                        md: 8,
                      },
                      name: 'contribuyente',
                      label: 'Contribuyente',
                      dataSource: contribuyenteFound,
                      onClear: resetAndValidateContribuyente,
                      render: (record) => padronToString(record, padronesEnum.CONTRIBUYENTE),
                    }}
                  />

                  <InputSearchPro
                    tooltip="Búsqueda por Nombre Comercial"
                    label="Comercio"
                    name="q_empresa"
                    onClickAdvanced={() => {
                      setTipoDePadron(padronesEnum.EMPRESA);
                      setVisibleBusquedaPadrones(true);
                    }}
                    breakPoints={{
                      xs: 24,
                      sm: 24,
                      md: 8,
                    }}
                    inputProps={{
                      onPressEnter: () => searchEmpresa(),
                      onChange: ({ target: { value } }) => {
                        if (!value) {
                          resetAndValidateEmpresa();
                        }
                      },
                    }}
                    resultProps={{
                      breakPoints: {
                        xs: 24,
                        sm: 24,
                        md: 8,
                      },
                      name: 'empresa',
                      label: 'Comercio',
                      dataSource: empresaFound,
                      onClear: resetAndValidateEmpresa,
                      render: (record) => padronToString(record, padronesEnum.EMPRESA),
                    }}
                  />
                </Row>
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Button
                      onClick={() => {
                        formSearch.resetFields();
                        setEmpresaFound([]);
                        setContribuyenteFound([]);
                        setPredioFound([]);
                        setData({ results: [], count: 0 });
                        setCurrentPage(1);
                      }}
                      icon={<DeleteOutlined />}
                      block
                    >
                      Limpiar búsqueda
                    </Button>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Button
                      htmlType="submit"
                      icon={<SearchOutlined />}
                      block
                    >
                      Buscar
                    </Button>
                  </Col>
                </Row>
              </Form>
              <Divider />
              <Table
                cols={columns}
                mobileColumns={[columns[0], columns[1]]}
                data={data.results || []}
                rowSelection={rowSelection}
                handleOnRowClick={handleOnRowClick}
                controls={{
                  onClickAdd,
                  onClickEdit,
                  onClickDelete,
                }}
                allowSearch={false}
                rowKey="id"
                baseURI={baseURI}
                permission={permission}
                pagination={false}
                paginationCallback={fetchData}
                count={data.count}
                currentPage={currentPage}
                hasAllViewPermission={hasAllViewPermission}
              />
            </>
          ) : (
            <Col span={24} style={{ height: '100%' }}>
              <Card
                className="form-container"
                title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Expediente de Anuncio`}
                extra={(
                  <FormSubmitControls
                    onFinish={getOnFinish()}
                    onCancel={onCancel}
                    allowSaveAndContinue
                    onCancelText="Cancelar"
                    permission={permission}
                  />
                )}
                bordered={false}
              >
                <Tabs
                  onChange={onChangeTabKey}
                  activeKey={currentTabKey}
                  type="card"
                >
                  <TabPane
                    tab={(
                      <span>
                        <InfoCircleOutlined />
                        Información General
                      </span>
                    )}
                    key="general"
                    forceRender
                  >
                    <Form
                      name="general"
                      layout="vertical"
                      form={form}
                      onFinish={() => onFinish()}
                      scrollToFirstError
                    >
                      <Row gutter={10}>

                        {!!selectedRowKeys.length && (
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="clave"
                              label="Clave"
                              hasFeedback
                            >
                              <Input allowClear disabled />
                            </Form.Item>
                          </Col>
                        )}
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="tipo_de_anuncio"
                            rules={rules.required}
                            label="Tipo de Anuncio"
                            hasFeedback
                          >
                            <Select dataSource={tiposDeAnuncios} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="ancho"
                            label="Ancho"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="alto"
                            label="Alto"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="vistas"
                            label="Vista"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={16}>
                          <Form.Item
                            name="mensaje"
                            label="Mensaje"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>

                        <InputSearchPro
                          label="Contribuyente"
                          onClickAdvanced={() => {
                            setTipoDePadron(padronesEnum.CONTRIBUYENTE);
                            setVisibleBusquedaPadrones(true);
                          }}
                          name="q_contribuyente"
                          rules={rules.required}
                          breakPoints={{
                            xs: 24,
                            sm: 24,
                            md: 8,
                          }}
                          inputProps={{
                            onPressEnter: () => searchContribuyente(),
                            onChange: ({ target: { value } }) => {
                              if (!value) {
                                resetAndValidate();
                              }
                            },
                          }}
                          resultProps={{
                            breakPoints: {
                              xs: 24,
                              sm: 24,
                              md: 8,
                            },
                            name: 'contribuyente',
                            label: 'Contribuyente',
                            dataSource: contribuyenteFound,
                            onClear: resetAndValidateContribuyente,
                            render: (record) => padronToString(record, padronesEnum.CONTRIBUYENTE),
                          }}
                        />

                        <Col xs={24} sm={24} md={16}>
                          <Form.Item
                            name="observaciones"
                            label="Observaciones"
                            hasFeedback
                          >
                            <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} allowClear />
                          </Form.Item>
                        </Col>
                        <InputSearchPro
                          tooltip="Búsqueda por Nombre Comercial"
                          label="Comercio"
                          onClickAdvanced={() => {
                            setTipoDePadron(padronesEnum.EMPRESA);
                            setVisibleBusquedaPadrones(true);
                          }}
                          name="q"
                          breakPoints={{
                            xs: 24,
                            sm: 24,
                            md: 8,
                          }}
                          inputProps={{
                            onPressEnter: () => searchEmpresa(),
                            onChange: ({ target: { value } }) => {
                              if (!value) {
                                resetAndValidate();
                              }
                            },
                          }}
                          resultProps={{
                            breakPoints: {
                              xs: 24,
                              sm: 24,
                              md: 8,
                            },
                            name: 'empresa',
                            label: 'Comercio',
                            dataSource: empresaFound,
                            onClear: resetAndValidate,
                            render: (record) => padronToString(record, padronesEnum.EMPRESA),
                          }}
                        />

                        <InputSearchPro
                          tooltip="Búsqueda por Nombre Comercial"
                          label="Predio"
                          onClickAdvanced={() => {
                            setTipoDePadron(padronesEnum.PREDIO);
                            setVisibleBusquedaPadrones(true);
                          }}
                          name="q"
                          breakPoints={{
                            xs: 24,
                            sm: 24,
                            md: 8,
                          }}
                          inputProps={{
                            onPressEnter: () => searchPredio(),
                            onChange: ({ target: { value } }) => {
                              if (!value) {
                                resetAndValidatePredio();
                              }
                            },
                          }}
                          resultProps={{
                            breakPoints: {
                              xs: 24,
                              sm: 24,
                              md: 8,
                            },
                            name: 'predio',
                            label: 'Predio',
                            dataSource: predioFound,
                            render: (record) => padronToString(record, padronesEnum.PREDIO),
                            onClear: resetAndValidatePredio,
                          }}
                        />
                        <Uploader
                          title="Evidencia"
                          limitMB={5}
                          formItemName="documentacion_de_evidencia"
                          allowedExtensions={['jpg', 'png', 'jpeg', 'pdf']}
                          clearFiles
                          dependencie={visible}
                        />

                      </Row>
                      <Divider orientation="left" plain>
                        <Typography.Title level={3}>Datos de Licencia</Typography.Title>
                      </Divider>
                      <Row gutter={10}>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="numero_licencia"
                            label="Número de Licencia"
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_de_emision"
                            label="Fecha de Autorización"
                          >
                            <DatePicker allowClear placeholder="" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_vencimiento_licencia"
                            label="Fecha de Vencimiento"
                          >
                            <DatePicker allowClear placeholder="" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="expediente_licencia"
                            label="Número de Expediente"
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="refrendo"
                            label="Refrendo"
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item hidden>
                        <Button htmlType="submit" />
                      </Form.Item>
                    </Form>
                  </TabPane>

                  {addressPermission?.view && (
                    <TabPane
                      tab={(
                        <>
                          <EnvironmentOutlined />
                          Dirección
                        </>
                      )}
                      key="direccion_anuncio"
                      forceRender={!!selectedRowKeys.length}
                      disabled={!selectedRowKeys.length}
                    >
                      <Direccion
                        key={current?.id}
                        currentURI={baseURI}
                        parentID={selectedRowKeys[0]}
                        setLoading={setLoading}
                        onError={onError}
                        setFormAddress={setFormAddress}
                        setCurrentTabKey={setCurrentTabKey}
                        addressPropName="direccion_anuncio"
                        callback={handleDireccionCallback}
                        permission={addressPermission}
                      />
                    </TabPane>
                  )}
                </Tabs>
              </Card>
            </Col>
          )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="Expediente de Anuncio"
          loading={loading}
        />
        <BusquedaAvanzadaPadrones
          visible={visibleBusquedaPadrones}
          setVisible={setVisibleBusquedaPadrones}
          callback={callbackBusquedaPadrones}
          tipoDePadron={tipoDePadron}
          allowAdd={false}
        />
      </Spin>
    </Row>
  );
};
export default ExpedienteDeAnuncio;
